import React, { Fragment } from 'react';
import ReactCountryFlag from 'react-country-flag';
//
import { alpha2CodeMap } from '../../../../constants/alpha2';
import numberFormat from '../../renderers/numberFormat';
import {
  GROUP_BY_FILTER_TYPES,
  GROUP_BY_TYPES_LABELS,
} from '../../../../constants/reports';

// using weakmap so that data itself can be the cache key
// and once data is collected by GC, it will disappear from cache automatically
const REPORT_AGGREGATE_CACHE = new WeakMap();

const sumAndFormatValues = (data) =>
  numberFormat(data.reduce((store, value = 0) => store + Number(value), 0));

const CACHEABLE_FOOTER_CELL =
  (aggregateFn) =>
  ({ table, column: { accessorFn, id } }) => {
    const rows = table.getFilteredRowModel().rows;
    if (!REPORT_AGGREGATE_CACHE.has(rows)) {
      REPORT_AGGREGATE_CACHE.set(rows, {});
    }
    const cached = REPORT_AGGREGATE_CACHE.get(rows);
    if (!(id in cached)) {
      cached[id] = aggregateFn(rows.map((row) => accessorFn(row.original)));
    }
    return cached[id];
  };

const FOOTER_CACHED_SUM_COLUMN = CACHEABLE_FOOTER_CELL(sumAndFormatValues);

const NUMBER_FORMAT_CELL = ({ cell }) => numberFormat(cell.getValue());
export const REPORT_COLUMN_DEFS = [
  {
    id: 'groupBy',
    header: '',
    columns: [
      {
        id: GROUP_BY_FILTER_TYPES.CREATED_DATE,
        accessorKey: 'createdDate',
        header: GROUP_BY_TYPES_LABELS[GROUP_BY_FILTER_TYPES.CREATED_DATE],
        enableResizing: true,
      },
      {
        id: GROUP_BY_FILTER_TYPES.CREATED_HOUR,
        accessorKey: 'createdDateHourly',
        header: GROUP_BY_TYPES_LABELS[GROUP_BY_FILTER_TYPES.CREATED_HOUR],
        enableResizing: true,
      },
      {
        id: GROUP_BY_FILTER_TYPES.WLID,
        accessorKey: 'whLabelUser',
        header: GROUP_BY_TYPES_LABELS[GROUP_BY_FILTER_TYPES.WLID],
        enableResizing: true,
      },
      {
        id: GROUP_BY_FILTER_TYPES.PUBLISHER,
        accessorKey: 'publisher',
        header: GROUP_BY_TYPES_LABELS[GROUP_BY_FILTER_TYPES.PUBLISHER],
        enableResizing: true,
      },
      {
        id: GROUP_BY_FILTER_TYPES.ENDPOINT,
        accessorKey: 'uPublisher',
        header: GROUP_BY_TYPES_LABELS[GROUP_BY_FILTER_TYPES.ENDPOINT],
        enableResizing: true,
      },
      {
        id: GROUP_BY_FILTER_TYPES.GEO,
        accessorKey: 'geo',
        header: GROUP_BY_TYPES_LABELS[GROUP_BY_FILTER_TYPES.GEO],
        size: 100,
        Cell: ({ cell }) => {
          if (!cell.getValue()) return null;

          const country = alpha2CodeMap.hasOwnProperty(cell.getValue())
            ? alpha2CodeMap[cell.getValue()]
            : {};

          return (
            <Fragment>
              <ReactCountryFlag
                className='emojiFlag'
                countryCode={cell.getValue()}
                style={{
                  fontSize: '2em',
                  lineHeight: '2em',
                  marginRight: '0.25rem',
                }}
                title={country.label}
              />
              {cell.getValue()}
            </Fragment>
          );
        },
      },
    ],
  },
  {
    id: 'base',
    header: '',
    columns: [
      {
        accessorKey: 'responses',
        header: 'Responses',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'spend',
        header: 'Spend',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'payout',
        header: 'Payout',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'discrepancy',
        header: 'Discrepancy',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
    ],
  },
  {
    id: 'impressions',
    header: 'Impressions',
    columns: [
      {
        accessorKey: 'impressions',
        header: 'Total',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'approvedImpressions',
        header: 'Approved',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'percentApprovedImpressions',
        header: '%',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
      },
      {
        accessorKey: 'winRate',
        header: 'Win Rate (%)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
      },
    ],
  },
  {
    id: 'invalidImpressions',
    header: 'Invalid Impressions',
    columns: [
      {
        accessorKey: 'percentRejectedImpressions',
        header: 'Total (%)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
      },
      {
        accessorKey: 'percentDuplicateImpressions',
        header: 'Duplicate (%)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
      },
      {
        accessorKey: 'percentExpiredImpressions',
        header: 'TTL (%)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
      },
      {
        accessorKey: 'percentIpImpressions',
        header: 'IP (%)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
      },
      {
        accessorKey: 'percentGeoImpressions',
        header: 'GEO (%)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
      },
      {
        accessorKey: 'percentDeviceImpressions',
        header: 'Device (%)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
      },
      {
        accessorKey: 'percentOsImpressions',
        header: 'OS (%)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
      },
      {
        accessorKey: 'percentOtherImpressions',
        header: 'Other (%)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
      },
    ],
  },
  {
    id: 'clicks',
    header: 'Clicks',
    columns: [
      {
        accessorKey: 'clicks',
        header: 'Total',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'approvedClicks',
        header: 'Approved',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'percentApprovedClicks',
        header: '%',
        Cell: NUMBER_FORMAT_CELL,
        enableResizing: true,
      },
      {
        accessorKey: 'ctr',
        header: 'CTR (%)',
        Cell: NUMBER_FORMAT_CELL,
        enableResizing: true,
      },
    ],
  },
  {
    id: 'invalidClicks',
    header: 'Invalid Clicks',
    columns: [
      {
        accessorKey: 'percentRejectedClicks',
        header: 'Total (%)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
      },
      {
        accessorKey: 'percentDuplicateClicks',
        header: 'Duplicate (%)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
      },
      {
        accessorKey: 'percentExpiredClicks',
        header: 'TTL (%)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
      },
      {
        accessorKey: 'percentIpClicks',
        header: 'IP (%)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
      },
      {
        accessorKey: 'percentGeoClicks',
        header: 'GEO (%)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
      },
      {
        accessorKey: 'percentDeviceClicks',
        header: 'Device (%)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
      },
      {
        accessorKey: 'percentOsClicks',
        header: 'OS (%)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
      },
      {
        accessorKey: 'percentNoimpClicks',
        header: 'No Impressions (%)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
      },
      {
        accessorKey: 'percentOtherClicks',
        header: 'Other (%)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
      },
    ],
  },
  {
    id: 'pixels',
    header: 'Pixels',
    columns: [
      {
        accessorKey: 'visitPixelsCount',
        header: 'Visit',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'cpaVisitPixels',
        header: 'CPA ($)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'lpViewPixelsCount',
        header: 'LP View',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'cpaLpViewPixels',
        header: 'CPA ($)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'lpClickPixelsCount',
        header: 'LP Click',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'cpaLpClickPixels',
        header: 'CPA ($)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'regStartedPixelsCount',
        header: 'RegStart',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'cpaRegStartedPixels',
        header: 'CPA ($)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'regFinishedPixelsCount',
        header: 'RegFin',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'cpaRegFinishedPixels',
        header: 'CPA ($)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'loginPixelsCount',
        header: 'Login',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'cpaLoginPixels',
        header: 'CPA ($)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'ftdPixelsCount',
        header: 'FTD',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'ftdPixelsAmount',
        header: 'FTD Amount ($)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'cpaFtdPixels',
        header: 'CPA ($)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'depositPixelsCount',
        header: 'Deposit',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'depositPixelsAmount',
        header: 'Deposit Amount ($)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'cpaDepositPixels',
        header: 'CPA ($)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'betPixelsCount',
        header: 'Bet',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'cpaBetPixels',
        header: 'CPA ($)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'withdrawalPixelsCount',
        header: 'Withdrawal',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'cpaWithdrawalPixels',
        header: 'CPA ($)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'otherPixelsCount',
        header: 'Other',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
      {
        accessorKey: 'cpaOtherPixelsCount',
        header: 'CPA ($)',
        enableResizing: true,
        Cell: NUMBER_FORMAT_CELL,
        Footer: FOOTER_CACHED_SUM_COLUMN,
      },
    ],
  },
  {
    id: 'moneyStats',
    header: '',
    columns: [
      {
        accessorKey: 'eCPM',
        header: 'eCPM ($)',
        Cell: NUMBER_FORMAT_CELL,
        enableResizing: true,
      },
      {
        accessorKey: 'eCPC',
        header: 'eCPC ($)',
        Cell: NUMBER_FORMAT_CELL,
        enableResizing: true,
      },
      {
        accessorKey: 'bidPrice',
        header: 'Bid price ($)',
        Cell: NUMBER_FORMAT_CELL,
        enableResizing: true,
      },
    ],
  },
];
